// The work-step review step's Workpaper(s) cell: a read-out of the row's
// `WorkStepWorkpaperDecision` with one menu entry that opens the Change
// Workpaper dialog, where the set is edited (and emptied). Separate from the
// controls `WorkpaperChip` because the states differ: a row is "Checking
// Optro…" until `useSeedWorkStepWorkpapers` has written its files in, and
// "None" is a deliberate choice (or a work step that simply holds no files)
// rather than a failed match.
import { ChevronDown, FileText } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../../../../components/ui/dropdown-menu";
import { WorkStepWorkpaperDecision } from "../../../../../models/audit/workStep";

interface ChipReading {
  label: string;
  // Hover text listing the files the label only counts.
  title: string | undefined;
  menuLabel: string;
  muted: boolean;
}

const PENDING_READING: ChipReading = {
  label: "Checking Optro…",
  title: undefined,
  menuLabel: "Edit",
  muted: true,
};

function readDecision(decision: WorkStepWorkpaperDecision): ChipReading {
  switch (decision.mode) {
    // Only reached when the row's candidates never arrived (the fetch failed,
    // or the dialog was reset while still loading): the backend resolves
    // every Optro file on the work step at import time instead.
    case "all_optro": {
      const uploads = decision.uploads.length;
      return {
        label:
          uploads === 0
            ? "Couldn't check Optro"
            : `Couldn't check Optro +${uploads} upload${uploads === 1 ? "" : "s"}`,
        title: [
          "Imports with every Optro file on this work step.",
          ...decision.uploads.map((f) => f.fileName),
        ].join("\n"),
        menuLabel: "Edit",
        muted: true,
      };
    }
    case "explicit": {
      const files = decision.files;
      if (files.length === 0) {
        return {
          label: "None",
          title: undefined,
          menuLabel: "Add",
          muted: true,
        };
      }
      return {
        label:
          files.length === 1 ? files[0].fileName : `${files.length} workpapers`,
        title:
          files.length === 1
            ? undefined
            : files.map((f) => f.fileName).join("\n"),
        menuLabel: "Edit",
        muted: false,
      };
    }
  }
}

interface WorkStepWorkpaperChipProps {
  decision: WorkStepWorkpaperDecision;
  // The row's Optro files are still being fetched; the menu is disabled
  // until they land and `decision` is the real one.
  pending: boolean;
  onEdit: () => void;
}

export const WorkStepWorkpaperChip = ({
  decision,
  pending,
  onEdit,
}: WorkStepWorkpaperChipProps) => {
  const reading = pending ? PENDING_READING : readDecision(decision);
  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        disabled={pending}
        className={`inline-flex items-center gap-1.5 text-sm ${
          reading.muted ? "text-stone-500" : ""
        }`}
        title={reading.title}
      >
        <FileText
          className={`h-4 w-4 ${
            reading.muted ? "text-stone-400" : "text-stone-500"
          }`}
        />
        {reading.label}
        <ChevronDown className="h-3.5 w-3.5 text-stone-400" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start">
        <DropdownMenuItem onClick={onEdit}>
          {reading.menuLabel}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
